import { supabase } from '@/lib/supabase/client';
import type {
  CashflowBeginningBalance,
  CashflowBeginningBalanceUpsert,
} from '@/types/cashflow-beginning-balances';

function logServiceError(context: string, error: unknown, metadata?: Record<string, unknown>) {
  if (import.meta.env.DEV) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;
    console.error(`[CashflowBeginningBalancesService] ${context}`, {
      error: errorMessage,
      stack: errorStack,
      ...metadata,
    });
  }
}

async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export const cashflowBeginningBalancesService = {
  async getAll(): Promise<CashflowBeginningBalance[]> {
    try {
      const { data, error } = await supabase
        .from('cashflow_beginning_balances')
        .select('*')
        .order('month_key', { ascending: true });

      if (error) {
        logServiceError('Error fetching beginning balances', error);
        return [];
      }

      return (data as CashflowBeginningBalance[]) ?? [];
    } catch (error) {
      logServiceError('Error fetching beginning balances', error);
      return [];
    }
  },

  async upsert(input: CashflowBeginningBalanceUpsert): Promise<CashflowBeginningBalance> {
    try {
      const userId = await getCurrentUserId();
      const isConfirmed = input.confirmed ?? false;
      const now = new Date().toISOString();

      const payload = {
        month_key: input.month_key,
        amount: input.amount,
        source: input.source ?? 'manual',
        source_label: input.source_label ?? null,
        qbo_realm_id: input.qbo_realm_id ?? null,
        qbo_environment: input.qbo_environment ?? null,
        qbo_as_of_date: input.qbo_as_of_date ?? null,
        qbo_account_ids: input.qbo_account_ids ?? [],
        confirmed: isConfirmed,
        confirmed_at: isConfirmed ? now : null,
        confirmed_by: isConfirmed ? userId : null,
        last_synced_at: input.last_synced_at ?? null,
        created_by: userId,
        updated_by: userId,
      };

      const { data, error } = await supabase
        .from('cashflow_beginning_balances')
        .upsert(payload, { onConflict: 'month_key' })
        .select()
        .single();

      if (error) {
        logServiceError('Error upserting beginning balance', error, { input });
        throw new Error(`Failed to save beginning balance: ${error.message}`);
      }

      if (!data) {
        throw new Error('Beginning balance was not returned after save');
      }

      return data as CashflowBeginningBalance;
    } catch (error) {
      logServiceError('Error upserting beginning balance', error, { input });
      throw error;
    }
  },
};
